import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Badge, Card, Row, Col, Container } from 'react-bootstrap';
import API from '@/common/API';
import { parsedUser } from '@/common/GetCurrentUser';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const AgentDetailsPage: React.FC = () => {
    const navigate = useHistory();
    const { id } = useParams<{ agentId: string }>();
    const [agent, setAgent] = useState<{
        id: string;
        name: string;
        email: string;
        avatar?: string;
        status: string;
        phone?: string;
        role?: string;
        account?: {
            firstName: string;
            lastName: string;
            email: string;
            pic?: string;
        };
        joinDate?: string;
        lastActive?: string;
        successRate?: number;
        avgCompletionTime?: number;
        specializations?: string[];
    } | null>(null);
    const [summary, setSummary] = useState<{
        totalVisits: number;
        totalPlaces: number;
        visitsThisMonth: number;
        placesThisMonth: number;
        placesWithDispatch: number;
        pointsWithSales: number;
    } | null>(null);
    const [recentPlaces, setRecentPlaces] = useState<Array<{
        id: string;
        name: string;
        address: string;
        category: string;
        status: string;
        createdAt: string;
    }>>([]);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const user = parsedUser();
    useEffect(() => {
        bindData();
    }, [id]);

    const bindData = async () => {
        try {

            setLoading(true);
            const response = await API.getAction("users/details?id=" + id + "&organizationId=" + user.OrganizationId);
            console.log(response);
            if (response.data.status === "ok") {
                setAgent(response.data.response.user);
                setSummary(response.data.response.summary);
                setRecentPlaces(response.data.response.recentPlaces);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }

    }

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    if (!agent) {
        return <div className="text-center text-red-500">Agent not found</div>;
    }

    return (
        <Container>
            <div style={{ padding: '1.5rem' }}>
                {/* Header Section */}
                <Card className="mb-4">
                    <Card.Body className="p-4">
                        <Row className="align-items-center">
                            <Col xs={12} md={8} className="mb-3 mb-md-0">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                    <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                        {agent.account ? (
                                            <img src={agent.account.pic} alt={agent.account.firstName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <span style={{ fontSize: '1.125rem', fontWeight: '600', color: '#4b5563' }}>
                                                {agent.account.firstName.split(' ').map(n => n[0]).join('')}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <h1 style={{ fontSize: '1.875rem', fontWeight: '700', marginBottom: '0.5rem' }}>{agent.account.firstName} {agent.account.lastName}</h1>
                                        {/* <Badge bg={agent.status === 'active' ? 'success' : 'secondary'} style={{ marginBottom: '0.5rem' }}>
                                            {agent.status}
                                        </Badge> */}
                                        <p style={{ color: '#4b5563', margin: 0 }}>{agent.account.email}</p>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} md={4}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fa fa-calendar" style={{ color: '#3b82f6', minWidth: '1rem' }}></i>
                                        <span>Joined {new Date(agent?.joinDate).toLocaleDateString()}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fa fa-clock" style={{ color: '#3b82f6', minWidth: '1rem' }}></i>
                                        <span>Last active {new Date(agent?.lastActive).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Performance Metrics */}
                <Row className="mb-4">
                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>{t("Total Visits")}</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{summary.totalVisits}</p>
                                    </div>
                                    <i className="fa fa-bar-chart" style={{ fontSize: '1.75rem', color: '#3b82f6' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>{t("Total Places")}</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{summary.totalPlaces}</p>
                                    </div>
                                    <i className="fa fa-check-circle" style={{ fontSize: '1.75rem', color: '#10b981' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>Success Rate</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{agent.successRate}%</p>
                                    </div>
                                    <i className="fa fa-chart-line" style={{ fontSize: '1.75rem', color: '#a855f7' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} lg={3} className="mb-3">
                        <Card className="h-100">
                            <Card.Body>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.5rem' }}>Avg. Time (hrs)</p>
                                        <p style={{ fontSize: '1.875rem', fontWeight: '700' }}>{agent.avgCompletionTime}</p>
                                    </div>
                                    <i className="fa fa-hourglass-half" style={{ fontSize: '1.75rem', color: '#f97316' }}></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Specializations and Recent Activity */}
                <Row className="mb-4">
                    {/* Specializations */}
                    <Col xs={12} lg={6} className="mb-3">
                        <Card>
                            <Card.Header>
                                <Card.Title style={{ marginBottom: 0 }}>{t("Visits")}</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    {agent.specializations?.map((spec, index) => (
                                        <Badge key={index} bg="info">{spec}</Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Recent Activity */}
                    <Col xs={12} lg={6} className="mb-3">
                        <Card>
                            <Card.Header>
                                <Card.Title style={{ marginBottom: 0 }}>{t("Recent Places")}</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {recentPlaces?.map((item) => (
                                        <div key={item.id} style={{ display: 'flex', gap: '1rem' }}>
                                            <div style={{ width: '0.5rem', height: '0.5rem', backgroundColor: '#3b82f6', borderRadius: '50%', marginTop: '0.25rem', flexShrink: 0 }}></div>
                                            <div style={{ flex: 1 }}>
                                                <p
                                                    onClick={() => {
                                                        navigate.push("/places/" + item.id);
                                                    }}
                                                    className='clickable' style={{ fontSize: '0.875rem', fontWeight: '500', margin: 0, marginBottom: '0.25rem' }}>{item.name}
                                                    <br />
                                                    <small className='text-muted'> {moment(item.createdAt).fromNow()}</small>
                                                </p>



                                                {/* <p style={{ fontSize: '0.75rem', color: '#4b5563', margin: 0, marginBottom: '0.25rem' }}>{activity.description}</p>
                                                <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>
                                                    {new Date(activity.timestamp).toLocaleString()}
                                                </p> */}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Container>
    );
};

export default AgentDetailsPage;